import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaUserPlus,
  FaSignInAlt,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaInfoCircle,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import Logo from "../../components/Logo";

const Register = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Références pour les champs du formulaire pour l'accessibilité
  const fullNameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    setMounted(true);

    // Mettre le focus sur le premier champ pour l'accessibilité
    if (fullNameRef.current) {
      fullNameRef.current.focus();
    }

    // Vérifier si un code d'affiliation est présent dans l'URL
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) {
      setAffiliateCode(refCode);
    }
  }, []);

  // Vérification de la force du mot de passe
  useEffect(() => {
    // Évaluer la force du mot de passe
    const calculatePasswordStrength = (password) => {
      if (!password) return 0;

      let strength = 0;
      // Au moins 8 caractères
      if (password.length >= 8) strength += 1;
      // Contient une lettre
      if (/[A-Za-z]/.test(password)) strength += 1;
      // Contient un chiffre
      if (/\d/.test(password)) strength += 1;
      // Contient un caractère spécial
      if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) strength += 1;

      return strength;
    };

    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  // Valider le formulaire avant soumission
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    // Validation du nom complet
    if (!fullName.trim()) {
      newErrors.fullName = "Le nom complet est requis";
      isValid = false;
    } else if (fullName.trim().length < 2) {
      newErrors.fullName = "Le nom complet doit contenir au moins 2 caractères";
      isValid = false;
    }

    // Validation de l'email
    if (!email.trim()) {
      newErrors.email = "L'email est requis";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Veuillez entrer un email valide";
      isValid = false;
    }

    // Validation du mot de passe
    if (!password) {
      newErrors.password = "Le mot de passe est requis";
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = "Le mot de passe doit contenir au moins 8 caractères";
      isValid = false;
    } else if (!/(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/.test(password)) {
      newErrors.password = "Le mot de passe doit contenir au moins une lettre et un chiffre";
      isValid = false;
    }

    // Validation de la confirmation du mot de passe
    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
      isValid = false;
    }

    // Validation des conditions d'utilisation
    if (!acceptTerms) {
      newErrors.acceptTerms = "Vous devez accepter les conditions d'utilisation";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Mettre le focus sur le premier champ avec erreur
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField === "fullName" && fullNameRef.current)
        fullNameRef.current.focus();
      if (firstErrorField === "email" && emailRef.current)
        emailRef.current.focus();
      if (firstErrorField === "password" && passwordRef.current)
        passwordRef.current.focus();

      return;
    }

    try {
      setLoading(true);

      // Format français pour compatibilité avec le backend mis à jour
      const userData = {
        nom: fullName, // Nous utilisons 'nom' au lieu de 'fullName'
        courriel: email, // Nous utilisons 'courriel' au lieu de 'email'
        'mot de passe': password, // Nous utilisons 'mot de passe' au lieu de 'password'
        affiliateCode: affiliateCode || undefined,
      };

      console.log('Envoi des données d\'inscription:', {
        ...userData,
        'mot de passe': '[MASQUÉ]',
      });

      const response = await register(userData);

      if (response) {
        toast.success(
          "Inscription réussie ! Un email de vérification a été envoyé.",
        );

        navigate("/email-verification-sent", {
          state: {
            email: email,
            fullName: fullName,
          },
        });
      }
    } catch (error) {
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message;
      toast.error(`Erreur: ${serverMessage}`);

      // Gérer les erreurs spécifiques du serveur
      if (error.response?.data?.field) {
        setErrors({
          ...errors,
          [error.response.data.field]: error.response.data.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  const floatingVariants = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Cercles décoratifs flottants */}
      <motion.div
        className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-green-300/20 to-blue-300/20 blur-3xl -top-20 -left-20"
        animate={{
          x: [0, 20, 0],
          y: [0, 30, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        style={{ zIndex: 1 }}
      />

      <motion.div
        className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-blue-300/20 to-purple-300/20 blur-3xl -bottom-40 -right-20"
        animate={{
          x: [0, -30, 0],
          y: [0, -20, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        style={{ zIndex: 1 }}
      />

      {/* Particules animées */}
      <motion.div className="absolute inset-0 z-0 opacity-30 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-white"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -Math.random() * 100 - 50],
              opacity: [0, 1, 0],
              scale: [0, Math.random() + 0.5, 0],
            }}
            transition={{
              duration: Math.random() * 3 + 3,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </motion.div>

      <motion.div
        className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden w-full max-w-md relative z-10 border border-white/40"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ zIndex: 50 }}
      >
        <div className="p-8">
          <motion.div
            className="flex justify-center mb-6"
            variants={floatingVariants}
            animate="animate"
          >
            <Logo size="large" animated={mounted} />
          </motion.div>

          <motion.h1
            className="text-2xl font-bold text-center text-gray-800 mb-6"
            variants={itemVariants}
          >
            Créer un compte
          </motion.h1>

          <motion.form
            className="space-y-4"
            variants={itemVariants}
            onSubmit={handleSubmit}
            noValidate
          >
            <div>
              <label
                htmlFor="fullName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nom complet
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="fullName"
                  ref={fullNameRef}
                  type="text"
                  autoComplete="name"
                  required
                  className={`block w-full pl-10 pr-3 py-2 border ${errors.fullName ? "border-red-500" : "border-gray-300"} rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  placeholder="Votre nom complet"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (errors.fullName)
                      setErrors({ ...errors, fullName: null });
                  }}
                  aria-invalid={errors.fullName ? "true" : "false"}
                  aria-describedby={
                    errors.fullName ? "fullName-error" : undefined
                  }
                />
              </div>
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600" id="fullName-error">
                  {errors.fullName}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Adresse email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  ref={emailRef}
                  type="email"
                  autoComplete="email"
                  required
                  className={`block w-full pl-10 pr-3 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"} rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  placeholder="exemple@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: null });
                  }}
                  aria-invalid={errors.email ? "true" : "false"}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600" id="email-error">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className={`block w-full pl-10 pr-10 py-2 border ${errors.password ? "border-red-500" : "border-gray-300"} rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  placeholder="Votre mot de passe"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password)
                      setErrors({ ...errors, password: null });
                  }}
                  aria-invalid={errors.password ? "true" : "false"}
                  aria-describedby="password-strength password-error"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600" id="password-error">
                  {errors.password}
                </p>
              )}

              {/* Indicateur de force du mot de passe */}
              <div className="mt-2" id="password-strength">
                <div className="flex items-center">
                  <div className="h-2 flex-1 bg-gray-200 rounded">
                    <div
                      className={`h-full rounded ${
                        passwordStrength === 0
                          ? "w-0"
                          : passwordStrength === 1
                            ? "w-1/4 bg-red-500"
                            : passwordStrength === 2
                              ? "w-2/4 bg-yellow-500"
                              : passwordStrength === 3
                                ? "w-3/4 bg-blue-500"
                                : "w-full bg-green-500"
                      }`}
                    />
                  </div>
                  <span className="ml-2 text-xs text-gray-500">
                    {passwordStrength === 0 && "Faible"}
                    {passwordStrength === 1 && "Faible"}
                    {passwordStrength === 2 && "Moyen"}
                    {passwordStrength === 3 && "Fort"}
                    {passwordStrength === 4 && "Très fort"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  <FaInfoCircle className="inline mr-1" />
                  Le mot de passe doit contenir au moins 8 caractères, une
                  majuscule, une minuscule et un chiffre.
                </p>
              </div>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className={`block w-full pl-10 pr-10 py-2 border ${errors.confirmPassword ? "border-red-500" : "border-gray-300"} rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                  placeholder="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword)
                      setErrors({ ...errors, confirmPassword: null });
                  }}
                  aria-invalid={errors.confirmPassword ? "true" : "false"}
                  aria-describedby={
                    errors.confirmPassword ? "confirmPassword-error" : undefined
                  }
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={
                    showConfirmPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p
                  className="mt-1 text-sm text-red-600"
                  id="confirmPassword-error"
                >
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="affiliateCode"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Code d'affiliation (optionnel)
              </label>
              <input
                id="affiliateCode"
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                placeholder="Code d'affiliation"
                value={affiliateCode}
                onChange={(e) => setAffiliateCode(e.target.value)}
              />
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  className={`h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded ${errors.acceptTerms ? "border-red-500" : ""}`}
                  checked={acceptTerms}
                  onChange={(e) => {
                    setAcceptTerms(e.target.checked);
                    if (errors.acceptTerms)
                      setErrors({ ...errors, acceptTerms: null });
                  }}
                  aria-invalid={errors.acceptTerms ? "true" : "false"}
                  aria-describedby={
                    errors.acceptTerms ? "terms-error" : undefined
                  }
                />
              </div>
              <div className="ml-3 text-sm">
                <label
                  htmlFor="acceptTerms"
                  className="font-medium text-gray-700"
                >
                  J'accepte les{" "}
                  <Link
                    to="/terms"
                    className="text-green-600 hover:text-green-500"
                  >
                    conditions d'utilisation
                  </Link>{" "}
                  et la{" "}
                  <Link
                    to="/privacy"
                    className="text-green-600 hover:text-green-500"
                  >
                    politique de confidentialité
                  </Link>
                </label>
                {errors.acceptTerms && (
                  <p className="mt-1 text-sm text-red-600" id="terms-error">
                    {errors.acceptTerms}
                  </p>
                )}
              </div>
            </div>

            <motion.button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Inscription en cours...
                </span>
              ) : (
                <span className="flex items-center">
                  <FaUserPlus className="mr-2" />
                  S'inscrire
                </span>
              )}
            </motion.button>
          </motion.form>

          <motion.div className="mt-6 text-center" variants={itemVariants}>
            <p className="text-sm text-gray-600">
              Déjà inscrit ?{" "}
              <Link
                to="/login"
                className="font-medium text-green-600 hover:text-green-500 flex items-center justify-center"
              >
                <FaSignInAlt className="mr-1" />
                Se connecter
              </Link>
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
